import React, { useEffect, useId, useRef } from 'react'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { usePresentation } from '../../context'
import { catalogZoneAllows } from './canvasCatalogDrop'
import { useCanvasDropZoneRegistry } from './canvasDropZoneRegistry'
import { BUILDER_V2_ACCENT, BUILDER_V2_MUTED_LABEL, BUILDER_V2_ZONE_LINE } from './builderV2Colors'
import classnames from 'classnames'

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      margin: theme.spacing(0, 3),
      height: 0,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      opacity: 0,
      pointerEvents: 'none',
      overflow: 'hidden',
      transition: 'height 120ms ease, opacity 120ms ease'
    },
    active: {
      // 44px rather than the original 30: the canvas hit-tests by nearest rail now, so this height
      // is purely how big a gap opens between sections mid-drag. The wider gap is what makes the
      // highlighted insertion point readable — at 30px the rails read as hairlines (#22688).
      height: 44,
      opacity: 1
    },
    line: {
      flex: '1 1 auto',
      height: 2,
      borderRadius: 1,
      background: BUILDER_V2_ZONE_LINE
    },
    lineOver: {
      background: BUILDER_V2_ACCENT
    },
    label: {
      flex: '0 0 auto',
      font: '500 11px Inter, sans-serif',
      color: BUILDER_V2_MUTED_LABEL,
      whiteSpace: 'nowrap'
    },
    labelOver: {
      color: BUILDER_V2_ACCENT
    }
  }),
  { name: 'CanvasDropZone' }
)

export interface CanvasDropZoneProps {
  /** Which kind of catalog drag this rail accepts — see canvasCatalogDrop.ts's `catalogZoneAllows`. */
  accepts: 'block' | 'section'
  onDrop: (dataTransfer: DataTransfer) => void
  classes?: StyleClasses<typeof useStyles>
  'data-testid'?: string
}

/**
 * A drop target that appears only while a catalog tile (Add panel Sections/Blocks) is being dragged,
 * labelled "New block"/"New section" (or "Drop <name> here" when it is the live target) — the
 * zone-rail affordance from the design mock (`Presentation Builder V2.dc.html` L802-1263, L2600-2680).
 *
 * Always mounted so the canvas doesn't reflow every time `catalogDrag`'s kind toggles between block
 * and section mid-session — it just collapses to 0 height when inactive or disallowed for the
 * current drag's kind.
 *
 * The rail carries no drag handlers of its own. It registers its rect and its `onDrop` with the
 * canvas root (`canvasDropZoneRegistry`), which hit-tests every rail at once and dispatches to the
 * nearest one. That is what fixed #22688's "dragging doesn't always drop": a rail that owned its own
 * `dragover` could only ever accept a release that landed inside its own few pixels.
 */
function CanvasDropZone(props: CanvasDropZoneProps) {
  const classes = useStyles(props)
  const { accepts, onDrop } = props
  const { catalogDrag } = usePresentation()
  const registry = useCanvasDropZoneRegistry()
  const register = registry?.register
  const id = useId()
  const ref = useRef<HTMLDivElement | null>(null)

  // the canvas calls onDrop long after registration, and this closure changes on every render
  const onDropRef = useRef(onDrop)
  onDropRef.current = onDrop

  useEffect(() => {
    if (!register) {
      return
    }

    return register(id, {
      accepts,
      getRect: () => {
        const el = ref.current
        if (!el) {
          return null
        }

        const rect = el.getBoundingClientRect()
        return { top: rect.top, bottom: rect.bottom }
      },
      onDrop: (dataTransfer) => onDropRef.current(dataTransfer)
    })
  }, [register, id, accepts])

  const live = !!catalogDrag && catalogZoneAllows(catalogDrag.kind, accepts)
  const isOver = live && registry?.activeZoneId === id

  if (!live) {
    return <div ref={ref} className={classes.root} data-testid={props['data-testid']} />
  }

  const label = isOver ? `Drop ${catalogDrag.label} here` : accepts === 'section' ? 'New section' : 'New block'

  return (
    <div ref={ref} className={classnames(classes.root, classes.active)} data-testid={props['data-testid']}>
      <span className={classnames(classes.line, { [classes.lineOver]: isOver })} />
      <span className={classnames(classes.label, { [classes.labelOver]: isOver })}>{label}</span>
      <span className={classnames(classes.line, { [classes.lineOver]: isOver })} />
    </div>
  )
}

export default CanvasDropZone
